import { useEffect, useState, type FormEvent } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { Eye, EyeOff, Loader2 } from 'lucide-react';
import { PlatformBackground } from '@/components/platform/PlatformBackground';
import { NerdyParadiseLogo } from '@shared/platform/logos';
import { useAuthStore } from '@/stores/authStore';
import { useAuth } from '@/hooks/useAuth';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

interface LoginRedirectState {
  /** The deep-linked location ProtectedRoute redirected from (see state.from). */
  from?: { pathname: string; search?: string; hash?: string };
}

/**
 * Where to send the user after a successful login. Prefers the deep link
 * ProtectedRoute captured in location.state.from; falls back to '/'. Never
 * redirects back into /login — that would be a redirect loop if `from`
 * somehow pointed at the login route itself.
 */
function resolveRedirectTarget(state: unknown): string {
  const from = (state as LoginRedirectState | null)?.from;
  if (!from || from.pathname === '/login') {
    return '/';
  }
  return `${from.pathname}${from.search ?? ''}${from.hash ?? ''}`;
}

export default function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { login, loginWithRedirect } = useAuth();
  const { isLoading, error, setLoading, setError, clearError } = useAuthStore();

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  // FR-006: access_denied = disallowed user → "Access not authorized"
  // FR-001b: oauth_failed = OAuth/provider failure → "Sign-in failed. Please try again."
  const errorParam = searchParams.get('error');
  const isAccessDenied = errorParam === 'access_denied';
  const isOAuthFailed = errorParam === 'oauth_failed';
  const oauthError = isAccessDenied || isOAuthFailed;
  const oauthErrorMessage = isAccessDenied
    ? 'Access not authorized.'
    : isOAuthFailed
      ? 'Sign-in failed. Please try again.'
      : oauthError
        ? 'Sign-in failed. Please try again.'
        : null;

  useEffect(() => {
    if (oauthErrorMessage) setError(oauthErrorMessage);
  }, [oauthErrorMessage, setError]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    clearError();

    if (!username || !password) {
      setError('Please enter both username and password');
      return;
    }

    setLoading(true);

    try {
      await login(username, password);
      navigate(resolveRedirectTarget(location.state));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = () => {
    clearError();
    loginWithRedirect();
  };

  return (
    <div className="theme-platform login-wrap flex min-h-screen w-full min-w-full">
      <PlatformBackground variant="login" />

      {/* Single vertical flow: Logo → Nerdy Paradise → text → Welcome back → Sign in → Apps/One/Yours */}
      <div className="login-content flex w-full min-h-screen justify-center items-center p-4 lg:p-8">
        <div className="login-page w-full max-w-md flex flex-col items-center text-center text-[#F0F0F5] shadow-2xl rounded-2xl bg-black/40 backdrop-blur-sm py-10 px-8">
          {/* 1. Logo — actual brand mark (sunrise + constellation),
               not the generic Sparkles glyph. The SVG carries its own
               rounded-square chamfer and gradient so we drop the
               outer dark box that used to wrap it. */}
          <div className="flex justify-center mb-6">
            <NerdyParadiseLogo
              size={80}
              className="drop-shadow-[0_8px_32px_rgba(255,107,74,0.35)]"
              title="Nerdy Paradise"
            />
          </div>
          {/* 2. Nerdy Paradise */}
          <h1 className="login-title-neon text-4xl font-bold mb-2">Nerdy Paradise</h1>
          <p className="tagline text-xl font-medium mb-4 text-[#FF2E97]">Enter Paradise</p>
          {/* 3. Text blob */}
          <p className="text-lg text-[#A0A0C0] mb-8">
            Your personal digital world. Sign in to access your apps.
          </p>
          {/* 4. Welcome back */}
          <div className="w-full mb-4">
            <h2 className="login-form-heading text-2xl font-bold tracking-tight">Welcome back</h2>
            <p className="login-form-sub mt-2">Sign in to your account to continue</p>
          </div>
          {/* OAuth error */}
          {oauthError && oauthErrorMessage && (
            <div className="w-full mb-4 rounded-lg bg-[#FF3B5C]/15 p-4 text-sm text-[#FF3B5C] flex flex-col gap-2">
              <span>{oauthErrorMessage}</span>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="border-[#00F0FF]/40 text-[#00F0FF] hover:bg-[#00F0FF]/10"
                onClick={() => {
                  clearError();
                  navigate('/login', { replace: true });
                }}
              >
                Retry
              </Button>
            </div>
          )}
          {/* 5. Sign in with Google */}
          <div className="w-full space-y-4 mb-8">
            <Button
              type="button"
              className="w-full btn-google"
              onClick={handleGoogleSignIn}
              disabled={isLoading}
            >
              Sign in with Google
            </Button>

            {(import.meta.env.DEV || import.meta.env.VITE_SHOW_DEV_LOGIN === 'true') && (
              <>
                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <span className="w-full border-t border-[#00F0FF]/20" />
                  </div>
                  <div className="relative flex justify-center text-xs uppercase">
                    <span className="bg-[#0D0D1A] px-2 login-form-sub">Or continue with</span>
                  </div>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4">
                  {error && !oauthError && (
                    <div className="rounded-lg bg-[#FF3B5C]/15 p-4 text-sm text-[#FF3B5C]">
                      {error}
                    </div>
                  )}

                  <div className="space-y-2">
                    <label
                      htmlFor="username"
                      className="text-sm font-medium leading-none login-form-heading"
                    >
                      Username
                    </label>
                    <input
                      id="username"
                      type="text"
                      value={username}
                      onChange={(e) => setUsername(e.target.value)}
                      placeholder="Enter your username"
                      disabled={isLoading}
                      className={cn(
                        'flex h-10 w-full rounded-md border border-[#00F0FF]/30 bg-[#1A1A2E] px-3 py-2 text-sm text-[#F0F0F5]',
                        'placeholder:text-[#A0A0C0]',
                        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#00F0FF]/50 focus-visible:ring-offset-2 focus-visible:ring-offset-[#0D0D1A]',
                        'disabled:cursor-not-allowed disabled:opacity-50'
                      )}
                    />
                  </div>

                  <div className="space-y-2">
                    <label
                      htmlFor="password"
                      className="text-sm font-medium leading-none login-form-heading"
                    >
                      Password
                    </label>
                    <div className="relative">
                      <input
                        id="password"
                        type={showPassword ? 'text' : 'password'}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder="Enter your password"
                        disabled={isLoading}
                        className={cn(
                          'flex h-10 w-full rounded-md border border-[#00F0FF]/30 bg-[#1A1A2E] px-3 py-2 pr-10 text-sm text-[#F0F0F5]',
                          'placeholder:text-[#A0A0C0]',
                          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#00F0FF]/50 focus-visible:ring-offset-2 focus-visible:ring-offset-[#0D0D1A]',
                          'disabled:cursor-not-allowed disabled:opacity-50'
                        )}
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-[#A0A0C0] hover:text-[#F0F0F5]"
                      >
                        {showPassword ? (
                          <EyeOff className="h-4 w-4" />
                        ) : (
                          <Eye className="h-4 w-4" />
                        )}
                        <span className="sr-only">
                          {showPassword ? 'Hide password' : 'Show password'}
                        </span>
                      </button>
                    </div>
                  </div>

                  <Button type="submit" className="w-full btn-google" disabled={isLoading}>
                    {isLoading ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Signing in...
                      </>
                    ) : (
                      'Sign in'
                    )}
                  </Button>
                </form>

                <div className="rounded-lg border border-[#00F0FF]/20 bg-[#1A1A2E]/80 p-4">
                  <p className="text-sm login-form-sub">
                    <strong className="login-form-heading">Demo credentials:</strong>
                    <br />
                    Username: <code className="text-[#00F0FF]">demo</code>
                    <br />
                    Password: <code className="text-[#00F0FF]">demo123</code>
                  </p>
                </div>
              </>
            )}
          </div>
          {/* 6. Apps / One / Yours */}
          <div className="grid grid-cols-3 gap-8 w-full justify-items-center text-center">
            <div>
              <div className="text-3xl font-bold text-[#00F0FF]">Apps</div>
              <div className="text-sm text-[#A0A0C0]">Your tools</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-[#FF2E97]">One</div>
              <div className="text-sm text-[#A0A0C0]">Platform</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-[#FFB800]">Yours</div>
              <div className="text-sm text-[#A0A0C0]">Personal</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
